// ============================================
// Auth Store - Zustand
// ============================================

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User, UserRole } from '@conai/shared-types';
import { hasPermission, hasAnyPermission, type PermissionKey } from '@conai/shared-constants';

interface AuthState {
  // State
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isHydrated: boolean;
  error: string | null;

  // Actions
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setHydrated: () => void;
  logout: () => void;
  reset: () => void;
}

const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: false, // Changed to false - we use isHydrated for initial state
  isHydrated: false,
  error: null,
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      ...initialState,

      setUser: (user) =>
        set({
          user,
          isAuthenticated: !!user,
          isLoading: false,
          error: null,
        }),

      setLoading: (isLoading) => set({ isLoading }),

      setError: (error) =>
        set({
          error,
          isLoading: false,
        }),

      setHydrated: () => set({ isHydrated: true }),

      logout: () => {
        // Clear tokens from both storages (handled by PersistentTokenStorage via api config)
        localStorage.removeItem('conai_admin_access_token');
        localStorage.removeItem('conai_admin_refresh_token');
        sessionStorage.removeItem('conai_admin_access_token');
        sessionStorage.removeItem('conai_admin_refresh_token');
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      },

      reset: () => set(initialState),
    }),
    {
      name: 'conai-admin-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Called after hydration is complete
        if (state) {
          state.setHydrated();
        }
      },
    }
  )
);

// ============================================
// Selectors
// ============================================

export const selectUser = (state: AuthState) => state.user;
export const selectIsAuthenticated = (state: AuthState) => state.isAuthenticated;
export const selectIsLoading = (state: AuthState) => state.isLoading;
export const selectIsHydrated = (state: AuthState) => state.isHydrated;
export const selectUserRole = (state: AuthState) => state.user?.role;

// ============================================
// Permission Helpers
// ============================================

/**
 * Check if current user has a specific permission
 */
export function useHasPermission(permission: PermissionKey): boolean {
  const role = useAuthStore(selectUserRole);
  if (!role) return false;
  return hasPermission(role, permission);
}

/**
 * Check if current user has any of the specified permissions
 */
export function useHasAnyPermission(permissions: PermissionKey[]): boolean {
  const role = useAuthStore(selectUserRole);
  if (!role) return false;
  return hasAnyPermission(role, permissions);
}

/**
 * Check if current user has a specific role
 */
export function useHasRole(allowedRoles: UserRole[]): boolean {
  const role = useAuthStore(selectUserRole);
  if (!role) return false;
  return allowedRoles.includes(role);
}

/**
 * Check if current user is admin (AI_ADMIN or STORE_ADMIN)
 */
export function useIsAdmin(): boolean {
  return useHasRole(['AI_ADMIN', 'STORE_ADMIN']);
}

/**
 * Check if current user is super admin (AI_ADMIN only)
 */
export function useIsSuperAdmin(): boolean {
  return useHasRole(['AI_ADMIN']);
}
